import { Link } from 'react-router';
import { CheckCircle2, FileCheck2, LockKeyhole, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const controls = [
  'Policy-backed retention controls with operational cleanup workflows',
  'Role-scoped access boundaries with admin-level auditability',
  'Encryption in transit and at rest across enterprise data flows',
  'Consent-centered intelligence sharing boundaries for sensitive relationship data',
];

const readiness = [
  'Security questionnaire and architecture response packet',
  'Documented data flow and processing-boundary model',
  'Operational runbooks for incident triage and retention actions',
  'Identity and SSO roadmap alignment for enterprise rollout',
];

export default function EnterpriseSecurity() {
  useSEO({
    title: 'Enterprise Security | TENSION for Work',
    description:
      'Review TENSION Enterprise security and compliance posture, including controls, retention operations, and pilot readiness.',
    canonical: '/enterprise/security',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    keywords: ['enterprise security', 'relationship wellness compliance', 'pilot security review', 'data retention controls'],
  });

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-5xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <img
            src="/branding/tension-enterprise-logo.svg"
            alt="TENSION Enterprise"
            className="mb-4 h-auto w-full max-w-md rounded-xl border border-slate-700/70 bg-slate-950/70 p-1"
          />
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Trust and security
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">Security posture built for procurement confidence</h1>
          <p className="mt-3 max-w-3xl text-slate-700 md:text-lg">
            This overview helps enterprise buyers assess controls, governance readiness, and operating discipline before legal,
            security, and IT approval cycles.
          </p>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          <Card className="enterprise-surface-card enterprise-lift-card p-6">
            <div className="enterprise-icon-chip">
              <ShieldCheck className="h-8 w-8 text-violet-600" aria-hidden />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Current controls</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {controls.map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="enterprise-surface-card enterprise-lift-card border-fuchsia-100/80 p-6 hover:shadow-fuchsia-500/10">
            <div className="inline-flex rounded-xl bg-fuchsia-100/75 p-2">
              <FileCheck2 className="h-8 w-8 text-fuchsia-600" aria-hidden />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Pilot readiness artifacts</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {readiness.map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <section className="enterprise-surface-card rounded-2xl p-6 md:p-8">
          <p className="enterprise-meta-pill mb-3 font-medium">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Security posture for pilot and procurement reviews
          </p>
          <div className="flex items-start gap-3">
            <LockKeyhole className="mt-0.5 h-5 w-5 text-violet-600" aria-hidden />
            <p className="text-sm leading-relaxed text-slate-700 md:text-base">
              Security and compliance statements on this page are operational summaries for evaluation and not legal
              certification claims. Contract-specific commitments are finalized in MSA/DPA processes.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/enterprise/pilot">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95">
                See pilot framework
              </Button>
            </Link>
            <Link to="/business-inquiries?inquiryType=enterprise">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Request security review
              </Button>
            </Link>
          </div>
        </section>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
